import { computed, Injectable, OnDestroy, Signal, signal } from "@angular/core";
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    distinctUntilChanged,
    filter,
    finalize,
    map,
    Observable,
    of,
    shareReplay,
    startWith,
    Subject,
    Subscription,
    tap,
} from "rxjs";
import { TradingService } from "src/app/services/http/trading.service";
import { AppConfig } from "src/app/app.config";
import { GeneralSelectionFacade, GeneralState } from "../general-selection/facade/general-selection.facade";
import { IAppState } from "../state/app.state";
import { select, Store } from "@ngrx/store";
import * as fromTrading from "../order/selectors/trading.selectors";
import * as TradingActions from "../order/actions/trading.actions";
import { InvestmentPlan, RegularInvestmentStatus } from "src/app/models/orders";
import { IPortfolio } from "src/app/models/portfolios";
import { IRegularInvestmentPlanSetting } from "src/app/models/featuresConfig";
import { deduplicatingSwitchMap } from "src/app/utils/deduplicatingSwitchMap";
import { SiteIds } from "src/app/models/sites";

@Injectable()
export class RegularInvestmentPlanFacade implements OnDestroy {
    private getAvailablePortfoliosSubscription: Subscription;
    private generalSelectionSubscription: Subscription;

    private selectedPortfolio: IPortfolio;
    private portfolioId: BehaviorSubject<number> = new BehaviorSubject(null);
    private portfolioId$: Observable<number> = this.portfolioId.asObservable();

    private isInvestmentPlanLoading = signal<boolean>(false);
    public isProductsLoading = signal<boolean>(false);
    public isInvestmentPlanOrProductsLoading = computed(
        () => this.isInvestmentPlanLoading() || this.isProductsLoading(),
    );

    private siteId = computed(() => this.appConfig.appConfigSignal().env?.siteId);

    protected regularWithdrawalFeatureSetting: Signal<Partial<IRegularInvestmentPlanSetting>> = computed(
        () => this.appConfig.appConfigSignal().features.regularInvestmentPlan,
    );

    protected regularInvestmentPlanFeatureEnabled = computed(() => this.regularWithdrawalFeatureSetting()?.enabled);

    /**
     * A subject that can be used to prompt a reload of regular investment plan.
     */
    public loadInvestmentPlan$ = new Subject<void>();
    public loadInvestmentPlanObservable$ = this.loadInvestmentPlan$.asObservable().pipe(startWith(null));

    constructor(
        private store: Store<IAppState>,
        private generalSelectionfacade: GeneralSelectionFacade,
        private appConfig: AppConfig,
        private tradingService: TradingService,
    ) {
        this.subscribeToGeneralSelectionState();
    }

    private subscribeToGeneralSelectionState() {
        this.generalSelectionSubscription = this.generalSelectionfacade.getSelectedEntityPortfolioState$.subscribe(
            (generalState: GeneralState) => {
                let selectedPortfolios = generalState.selectedPortfolios;
                this.setSelectPortfolio(selectedPortfolios);
            },
        );
    }

    private setSelectPortfolio(genrealSelectionSelectedPortfolios: IPortfolio[]): void {
        if (!!genrealSelectionSelectedPortfolios && genrealSelectionSelectedPortfolios.length > 0) {
            this.selectedPortfolio = genrealSelectionSelectedPortfolios[0];
            this.portfolioId.next(this.selectedPortfolio.id);

            //This is needed to preserve existing logic that relies on the investment plan in the state being updated
            //when the selected entity/portfolio changes.
            this.loadInvestmentPlanForPortolio(this.selectedPortfolio.id);
        } else {
            this.portfolioId.next(null);
            this.selectedPortfolio = null;
        }
    }

    /**
     * An observable that returns the available products for the selected portfolio.
     * The products will automatically reload when the portfolio id changes.
     * 
     * This probably shouldnt be in this facade but it is closely tied with the regular investment plan and I was 
     * having issues with the automatic reload on portfolio id change in the trading facade as the portfolio id is
     * derived differently.
     */
    public products$ = this.portfolioId$.pipe(
        filter((portfolioId) => !!portfolioId),
        distinctUntilChanged(),
        tap(() => {
            this.isProductsLoading.set(true);
        }),
        deduplicatingSwitchMap((portfolioId) => {
            return this.tradingService.getProducts(portfolioId).pipe(
                finalize(() => {
                    this.isProductsLoading.set(false);
                }),
                catchError(() => of(null)),
            );
        }),
        shareReplay(1),
    );

    /**
     * An observable that returns the regular investment plan for the selected portfolio.
     * The investment plan will reload either when the portfolio id changes
     * or the load invesmtent plan subject emits IF regular investment plans are enabled for the manager.
     */
    public investmentPlan$ = combineLatest([this.portfolioId$, this.loadInvestmentPlanObservable$]).pipe(
        filter(([portfolioId, _]) => !!portfolioId && this.regularInvestmentPlanFeatureEnabled()),
        distinctUntilChanged(),
        map((x) => x[0]),
        tap(() => {
            this.isInvestmentPlanLoading.set(true);
        }),
        deduplicatingSwitchMap((portfolioId) => {
            return this.tradingService.getRegularInvestmentPlan(portfolioId).pipe(
                finalize(() => {
                    this.isInvestmentPlanLoading.set(false);
                }),
                catchError(() => {
                    return of(null);
                }),
            );
        }),
        shareReplay(1),
    );

    /**
     * Performs a retrieval of the regular investment plan for the given portfolio id.
     */
    public getInvestmentPlan(portfolioId: number): Observable<InvestmentPlan | null> {
        return this.tradingService.getRegularInvestmentPlan(portfolioId).pipe(
            catchError(() => {
                return of(null);
            }),
        );
    }

    /**
     * Performs a save of a regular investment plan by calling directly to the service.
     * This avoids additional side actions from being performed when saving through dispatching the action.
     */
    public saveRegularInvestmentPlan(regularInvestmentPlan: InvestmentPlan): Observable<any> {
        return this.tradingService.saveRegularInvestmentPlan(regularInvestmentPlan);
    }

    /**
     * Attempts to retrieve the regular investment plan from state. If there is no
     * plan in state dispatches an action to load the investment plan for the selected entity.
     */
    public getRegularInvestmentPlan(): Observable<Partial<InvestmentPlan>> {
        return this.store.pipe(
            select(fromTrading.selectRegularInvestmentPlan),
            tap((investmentPlan: InvestmentPlan) => {
                if (!investmentPlan) {
                    this.loadInvestmentPlanForEntity();
                }
            }),
        );
    }

    /**
     * Dispatches an action to request the regular investment plan for the selected portfolio
     */
    private loadInvestmentPlanForEntity(): void {
        if (this.getAvailablePortfoliosSubscription){
            this.getAvailablePortfoliosSubscription.unsubscribe();
        }

        this.getAvailablePortfoliosSubscription = this.portfolioId$
            .subscribe((portfolioId: number) => {
                if (portfolioId) {
                    this.loadInvestmentPlanForPortolio(portfolioId);
                } 
                //I didnt understand this logic but it seems to be really important to ensure the KiwiSaver
                //redirection works correctly for KiwiSaver sites so shouldn't be removed.
                else if(this.siteId() == SiteIds.INVESTNOW || this.siteId() == SiteIds.INVESTNOWCOMBINED || this.siteId() == SiteIds.EVIDENTIAL) {
                    this.store.dispatch(new TradingActions.GetRegularInvestmentPlanNotFound());
                }
            });
    }

    private loadInvestmentPlanForPortolio(portfolioId: number): void {
        if (!this.regularInvestmentPlanFeatureEnabled()) return;

        this.store.dispatch(new TradingActions.GetRegularInvestmentPlan(portfolioId));
    }

    public dispatchSaveRegularInvestmentPlanAction(payload: InvestmentPlan): void {
        this.store.dispatch(new TradingActions.SaveRegularInvestmentPlan(payload));
    }

    /**
     * Dispatches the same actions as the trading effect on successful update of a regular invesmtent plan.
     */
    public regularInvestmentPlanUpdateSuccessfulActions(investmentPlan: InvestmentPlan): void {
        this.store.dispatch(new TradingActions.GetRegularInvestmentPlanSuccess(investmentPlan));
        this.store.dispatch(new TradingActions.UpdateStatus());
    }

    /**
     * Dispatches the same actions as the trading effect on unsuccessful update of a regular invesmtent plan.
     * @param investmentPlan the regular investment plan passed to the effect to be saved. I am not sure if the status was persisted on a failed update.
     */
    public regularInvestmentPlanUpdateUnsuccessfulActions(investmentPlan: InvestmentPlan): void {
        this.store.dispatch(
            new TradingActions.GetRegularInvestmentPlanSuccess({
                ...investmentPlan,
                status: RegularInvestmentStatus.Incomplete,
            }),
        );
        this.store.dispatch(new TradingActions.UpdateStatus());
    }

    ngOnDestroy(): void {
        this.getAvailablePortfoliosSubscription.unsubscribe();
        this.generalSelectionSubscription.unsubscribe();
    }
}
